import {useState} from 'react';
import {Control, Controller, FieldValues, Path} from "react-hook-form";
import {DateRangePicker, DateRangeProps, DefinedRangeProps} from 'react-date-range';

//date-fns
import {es} from 'date-fns/locale';
import {format} from 'date-fns';

// CSS
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import './styles/ControlledDateRangePicker.css'

// MUI
import {Button, Popover, Stack, TextField, useTheme} from "@mui/material";
import {Event} from '@mui/icons-material';
import {defaultInputRanges, defaultStaticRanges} from "./utils/staticRanges";

/**
 * ControlledDateRangePickerProps interface
 *
 * @template T - A generic parameter that extends FieldValues from react-hook-form
 *
 * @property {Control<T>} control - The control object from react-hook-form
 * @property {Path<T>} startDateName - The field name for the start date
 * @property {Path<T>} endDateName - The field name for the end date
 * @property {string} [label] - An optional label for the date range picker
 * @property {(date: Date | string | null) => string} [dateFormatter] - An optional function to format the date. If provided, it will be used instead of the default formatter (dd/MM/yyyy)
 *
 */

export interface ControlledDateRangePickerProps<T extends FieldValues> extends DateRangeProps/*, DefinedRangeProps*/ {
    control: Control<T>;
    startDateName: Path<T>,
    endDateName: Path<T>,
    label?: string,
    dateFormatter?: (date: Date | string | null) => string,
}

/**
 * ControlledDateRangePicker component to be used in react-hook-form forms
 *
 * @template T - A generic parameter that extends FieldValues from react-hook-form
 *
 * @param {ControlledDateRangePickerProps<T>} props - The props that define the ControlledDateRangePicker component
 */
export const ControlledDateRangePicker = <T extends FieldValues,> ({
                                                                       control,
                                                                       startDateName,
                                                                       endDateName,
                                                                       label,
                                                                       dateFormatter
                                                                   }: ControlledDateRangePickerProps<T>) => {
    const theme = useTheme()

    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

    const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = !!anchorEl;

    const toShortDate = (date: Date | string | null): string => {
        if (!date) return '';

        return dateFormatter ? dateFormatter(date) : format(new Date(date), 'dd/MM/yyyy');
    };

    return (
        <>
            {
                // We nest Controller components to control each date separately
            }
            <Controller
                control={control}
                name={startDateName}
                render={({field: {onChange: onStartDateChange, value: valueStart}}) => (

                    <Controller
                        control={control}
                        name={endDateName}
                        render={({field: {onChange: onEndDateChange, value: valueEnd}}) => (
                            <>
                                <TextField
                                    label={label || 'Fecha Desde - Hasta'}
                                    value={`${toShortDate(valueStart)} - ${toShortDate(valueEnd )}`}
                                    onClick={(e) => handleClick(e)}
                                    InputProps={{
                                        readOnly: true,
                                        endAdornment: <Event/>
                                    }}
                                    size={'small'}
                                />
                                <Popover
                                    id={'date-picker-popover'}
                                    open={open}
                                    anchorEl={anchorEl}
                                    onClose={handleClose}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                    }}
                                >
                                    <Stack direction={'column'}
                                           alignItems={'flex-end'}
                                    >
                                        <DateRangePicker
                                            onChange={(item) => {
                                                const {startDate, endDate} = item.selection;
                                                onStartDateChange(startDate);
                                                onEndDateChange(endDate);
                                            }}
                                            moveRangeOnFirstSelection={false}
                                            retainEndDateOnFirstSelection={false}
                                            months={2} //number of calendar months to display
                                            ranges={[
                                                {
                                                    startDate: valueStart || new Date(),
                                                    endDate: valueEnd || new Date(),
                                                    key: 'selection',
                                                    color: theme.palette.primary.main
                                                },
                                            ]}
                                            direction="horizontal"
                                            preventSnapRefocus={true}
                                            calendarFocus="backwards"
                                            locale={es}
                                            staticRanges={defaultStaticRanges}
                                            color={theme.palette.primary.main}
                                            showPreview
                                            inputRanges={defaultInputRanges}
                                        />
                                        <Stack direction={'row'}>
                                            <Button onClick={() => {
                                                onEndDateChange(undefined)
                                                onStartDateChange(undefined)
                                            }}>
                                                Limpiar
                                            </Button>

                                            <Button onClick={handleClose}
                                                    sx={{width: 1 / 4}}
                                            >
                                                Cerrar
                                            </Button>
                                        </Stack>
                                    </Stack>
                                </Popover>
                            </>
                        )}
                    />
                )}
            />
        </>
    )
}



